import { Text, Tooltip, HStack, Icon, Box, useColorModeValue } from "@chakra-ui/react";
import React from "react";
import { FiClock, FiCalendar } from "react-icons/fi";
import { useTranslation } from "react-i18next";

interface TimeAgoProps {
  date: string;
}

const TimeAgo: React.FC<TimeAgoProps> = ({ date }) => {
  const { t } = useTranslation();

  // Simple time ago function
  const getTimeAgo = (dateString: string): string => {
    const now = new Date();
    const past = new Date(dateString);
    const diff = now.getTime() - past.getTime();

    // Convert to seconds, minutes, hours, days
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 30) {
      const months = Math.floor(days / 30);
      return months === 1
        ? t('common.timeAgo.oneMonth')
        : t('common.timeAgo.months', { count: months });
    }
    if (days > 0) {
      return days === 1
        ? t('common.timeAgo.oneDay')
        : t('common.timeAgo.days', { count: days });
    }
    if (hours > 0) {
      return hours === 1
        ? t('common.timeAgo.oneHour')
        : t('common.timeAgo.hours', { count: hours });
    }
    if (minutes > 0) {
      return minutes === 1
        ? t('common.timeAgo.oneMinute')
        : t('common.timeAgo.minutes', { count: minutes });
    }
    return t('common.timeAgo.justNow');
  };

  // Format the date for the tooltip
  const formatDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Determine color based on recency
  const getColorScheme = (): string => {
    const now = new Date();
    const past = new Date(date);
    const diffDays = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return "green"; // Today
    if (diffDays < 3) return "teal"; // Recent
    if (diffDays < 7) return "blue"; // This week
    if (diffDays < 30) return "purple"; // This month
    return "gray"; // Older
  };

  const colorScheme = getColorScheme();
  const textColor = useColorModeValue(`${colorScheme}.700`, `${colorScheme}.300`);
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.400`);
  const bgColor = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const timeText = getTimeAgo(date);
  const formattedDate = formatDate(date);

  return (
    <Tooltip
      label={formattedDate}
      hasArrow
      placement="top"
      bg={useColorModeValue("gray.100", "gray.700")}
    >
      <HStack
        spacing={1}
        py={1}
        px={2}
        borderRadius="md"
        bg={bgColor}
        transition="all 0.2s ease"
        _hover={{
          transform: "translateY(-1px)",
          boxShadow: "sm"
        }}
        display="inline-flex"
      >
        <Icon as={FiClock} color={iconColor} fontSize="xs" />
        <Text
          fontSize="sm"
          fontWeight="medium"
          color={textColor}
        >
          {timeText}
        </Text>
      </HStack>
    </Tooltip>
  );
};

export default TimeAgo;